import { JobContextActions } from '../TeamChannelHeader/JobContextHeader';
import type { JobContextHeaderData } from '../TeamChannelHeader/JobContextHeader';

import type {
  BookingSession,
  BookingSessionStatus,
  BookingUser,
} from '@/features/bookings-management/types';
import { formatAddress } from '@/utils/formatAddress';
import { fDate } from '@/utils/formatTime';

type JobDetailsPanelProps = {
  context: JobContextHeaderData;
};

type DetailRow = {
  label: string;
  value: string;
};

const emptyValue = 'N/A';

const formatName = (user?: Partial<BookingUser>): string => {
  if (!user) return emptyValue;

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || emptyValue;
};

const formatDate = (value?: string) => {
  if (!value) return emptyValue;

  try {
    return fDate(value, 'dd MMM yyyy');
  } catch {
    return value;
  }
};

const formatAddressValue = (session: BookingSession) => {
  const address = session.address;
  if (!address) return emptyValue;

  if (address.formatted_address) {
    return formatAddress(
      address.formatted_address,
      address.postcode
    ).formattedAddressWithPostcode;
  }

  return (
    [
      address.line_1,
      address.line_2,
      address.town_or_city,
      address.postcode,
    ]
      .filter(Boolean)
      .join(', ') || emptyValue
  );
};

const formatDisplayValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return emptyValue;

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) {
    return value.length ? value.map(formatDisplayValue).join(', ') : emptyValue;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const display =
      record.display ??
      record.name ??
      record.label ??
      record.value ??
      record.ref ??
      null;

    return display ? String(display) : emptyValue;
  }

  return String(value);
};

const formatHours = (value: unknown): string => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return emptyValue;
  if (parsed === 1) return '1 hour';
  return `${parsed} hours`;
};

const getStatusTone = (status?: BookingSessionStatus): string => {
  const styleClass = status?.style_class?.toLowerCase();

  if (styleClass === 'success' || styleClass === 'warning') return styleClass;
  if (styleClass === 'error') return 'error';
  if (styleClass === 'info' || styleClass === 'primary') return 'info';

  return 'default';
};

const getCleanerName = (session: BookingSession): string => {
  const firstCleaner = session.booking_session_cleaners?.[0];
  return firstCleaner ? formatName(firstCleaner) : 'Unassigned';
};

const getServiceName = (session: BookingSession): string => {
  const service = session.service_variant?.service?.name;
  const variant = session.service_variant?.name;

  return [service, variant].filter(Boolean).join(' - ') || emptyValue;
};

const getNotes = (session: BookingSession): DetailRow[] =>
  [
    {
      label: 'Access instructions',
      value: formatDisplayValue(session.access_instructions),
    },
    {
      label: 'Provider instructions',
      value: formatDisplayValue(session.provider_instructions),
    },
    { label: 'Admin notes', value: formatDisplayValue(session.admin_notes) },
    {
      label: 'Internal notes',
      value: formatDisplayValue(session.internal_notes),
    },
  ].filter(({ value }) => value && value !== emptyValue);

const getRows = (session: BookingSession): DetailRow[] => [
  { label: 'Client', value: formatName(session.user) },
  { label: 'Cleaner', value: getCleanerName(session) },
  { label: 'Date', value: formatDate(session.date) },
  { label: 'Time', value: session.time || emptyValue },
  { label: 'Address', value: formatAddressValue(session) },
  { label: 'Access type', value: formatDisplayValue(session.access_type) },
  { label: 'Service', value: getServiceName(session) },
  { label: 'Property', value: formatDisplayValue(session.property_type) },
  { label: 'Bedrooms', value: formatDisplayValue(session.bedrooms) },
  { label: 'Bathrooms', value: formatDisplayValue(session.bathrooms) },
  { label: 'Hours', value: formatHours(session.total_hours ?? session.hours) },
  {
    label: 'Cleaning products',
    value: formatDisplayValue(session.cleaning_products),
  },
  { label: 'Extras', value: formatDisplayValue(session.extras) },
  { label: 'Pets', value: formatDisplayValue(session.pets) },
];

const JobDetailsPanelLoading = () => (
  <aside className="job-details-panel" aria-label="Loading job details">
    <div className="job-details-panel__header">
      <div>
        <p className="job-details-panel__eyebrow">Job details</p>
        <h2>Loading context...</h2>
      </div>
    </div>
    <div className="job-details-panel__skeleton" />
    <div className="job-details-panel__skeleton short" />
    <div className="job-details-panel__skeleton" />
  </aside>
);

export const JobDetailsPanel = ({ context }: JobDetailsPanelProps) => {
  const { isLoading, links, session } = context;

  if (isLoading) return <JobDetailsPanelLoading />;
  if (!session) return null;

  const rows = getRows(session);
  const notes = getNotes(session);
  const statusTone = getStatusTone(session.booking_session_status);
  const paymentTone = getStatusTone(session.booking_session_payment_status);

  return (
    <aside className="job-details-panel" aria-label="Job details">
      <div className="job-details-panel__header">
        <div>
          <p className="job-details-panel__eyebrow">Job details</p>
          <h2>Session #{session.id}</h2>
        </div>
        <JobContextActions links={links} />
      </div>

      <div className="job-details-panel__status-row">
        <span
          className={`job-details-panel__status job-details-panel__status--${statusTone}`}
        >
          {session.booking_session_status?.display ?? emptyValue}
        </span>
        <span
          className={`job-details-panel__status job-details-panel__status--${paymentTone}`}
        >
          {session.booking_session_payment_status?.display ?? emptyValue}
        </span>
      </div>

      <section className="job-details-panel__section">
        <h3>Operational Summary</h3>
        <dl className="job-details-panel__details">
          {rows.map(({ label, value }) => (
            <div className="job-details-panel__row" key={label}>
              <dt>{label}</dt>
              <dd title={value}>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {notes.length > 0 && (
        <section className="job-details-panel__section">
          <h3>Notes</h3>
          {notes.map(({ label, value }) => (
            <div className="job-details-panel__note" key={label}>
              <p>{label}</p>
              <span>{value}</span>
            </div>
          ))}
        </section>
      )}
    </aside>
  );
};
